import { Formik } from "formik";
import { useState } from "react";
import { getSelectOptionsFromEnum, showToast } from "../../../../../utils/miscFunctions";
import { completePayout } from "../../../../../api/requests";
import SelectDropdown from "../../../../../components/form-control/SelectDropdown";
import { PAYMENT_CHANNELS } from "../../../../../resources/enums";
import Button from "../../../../../components/form-control/Button";
import Input from "../../../../../components/form-control/Input";
import * as Yup from "yup";
import DatePickerInput from "../../../../../components/form-control/DatePickerInput";
import { getFormattedDate } from "../../../../../utils/miscFunctions";
import { Payout } from "../../../../../models/payout/Payout";
import { CheckCircle2, XCircleIcon } from "lucide-react";

interface CompleteDisbursementProps {
    onProceed: () => void;
    onClose: () => void;
    payout: Payout;
}

const CompletePayout = ({ onProceed, onClose, payout }: CompleteDisbursementProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    return (
        <div className="">
            <Formik
                initialValues={{
                    paymentAmount: payout?.netToPay ?? 0,
                    paymentChannel: '',
                    paymentDate: null,
                    paymentReference: ''
                }}
                enableReinitialize={true}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({
                    paymentAmount: Yup.number().required('Payment amount is required.'),
                    paymentChannel: Yup.string().trim().required('Payment Channel is required.'),
                    paymentDate: Yup.string().trim().required('Date of transaction is required.').nullable(),
                    paymentReference: Yup.string().trim().required('Payment reference is required.')
                })}
                onSubmit={async (values) => {
                    const payload: any = {
                        paymentAmount: values.paymentAmount,
                        paymentChannel: values.paymentChannel,
                        paymentDate: getFormattedDate({ date: values.paymentDate, toServer: true }),
                        paymentReference: values.paymentReference
                    };

                    try {
                        setLoading(true);
                        const response: any = await completePayout(Number(payout.id), payload);
                        if (response) {
                            showToast.success(response?.message || 'Disbursement completed successfully');
                            onProceed();
                        }
                    } catch (error: any) {
                        showToast.error(error?.description || 'Failed to complete payout');
                    } finally {
                        setLoading(false);
                    }
                }}>
                {({ values, errors, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => (
                    <>
                        <div className="p-6">
                            <p className="text-sm text-gray-700 mb-4 dark:text-darkBg-mainText">
                                Confirm that you have received the payout and want to mark it as paid in the system.
                            </p>
                            <div className="bg-gray-50 p-4 rounded-md mb-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Payout Reference:</span>
                                        <span className="font-medium text-gray-900">{payout ? payout?.reference : '-'}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Amount:</span>
                                        <span className="font-medium text-gray-900">GHS {payout?.netToPay || '-'}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Current Status:</span>

                                        {payout && payout?.isPaid
                                            ?
                                            <div className='flex items-center w-fit gap-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                                                <CheckCircle2 className='h-4 w-4 text-green-400' />
                                                <span className=' font-semibold  truncate'>Paid</span>
                                            </div>
                                            :
                                            <div className='flex items-center w-fit gap-2 bg-red-100 text-red-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                                                <XCircleIcon className='h-4 w-4 text-red-400' />
                                                <span className=' font-semibold  truncate'>Unpaid</span></div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <form className="px-6" onSubmit={handleSubmit}>
                            <div className="w-full space-y-5 ">
                                <Input
                                    type={'number'}
                                    name="paymentAmount"
                                    value={values.paymentAmount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Payment amount"
                                    placeholder="Enter payment amount"
                                    required
                                    error={touched.paymentAmount ? errors.paymentAmount : undefined}
                                    helperText={touched?.paymentAmount ? errors.paymentAmount : undefined}
                                    className="rounded"
                                />

                                <Input
                                    type={'text'}
                                    name="paymentReference"
                                    value={values.paymentReference}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Payment reference"
                                    placeholder="Enter payment reference"
                                    required
                                    error={touched.paymentReference ? errors.paymentReference : undefined}
                                    helperText={touched?.paymentReference ? errors.paymentReference : undefined}
                                    className="rounded"
                                />


                                <SelectDropdown
                                    name="paymentChannel"
                                    listSelectedValue={values.paymentChannel}
                                    onChange={(value: string) => {
                                        setFieldValue('paymentChannel', value);

                                    }}
                                    label="Payment channel"
                                    placeholder="Select payment channel"
                                    list={getSelectOptionsFromEnum(PAYMENT_CHANNELS)}
                                    required
                                    error={touched.paymentChannel ? errors.paymentChannel : undefined}
                                    helperText={touched?.paymentChannel ? errors.paymentChannel : undefined}
                                    selectInputClassName="rounded"
                                />

                                <div className="w-full">
                                    <DatePickerInput
                                        id="paymentDate"
                                        selected={values.paymentDate}
                                        onChange={(date) => setFieldValue('paymentDate', date)}
                                        classes=''
                                        dateFormat="MM-dd-yyyy"
                                        label="Date of Transaction"
                                        showYearDropdown
                                        allowEarlierSelections={true}
                                        allowFutureSelections={false}
                                        error={touched.paymentDate ? errors.paymentDate : undefined}
                                        helperText={touched.paymentDate ? errors.paymentDate : undefined}
                                        required
                                    />
                                </div>

                            </div>


                            <div className="w-full flex gap-4 mt-6 !mb-4">
                                <Button
                                    type="button"
                                    label="Cancel"
                                    onClick={onClose}
                                    bgColor='secondary'
                                    fullWidth
                                />
                                <Button
                                    label="Continue"
                                    onClick={handleSubmit}
                                    bgColor='primary'
                                    fullWidth
                                    loading={loading}
                                    disabled={loading}
                                />
                            </div>
                        </form >
                    </>
                )}
            </Formik >
        </div >
    );
};

export default CompletePayout;
